import { COLLECTION_VIEW_DEFAULT } from "@/constants/collection"
import {
  CollectionFilterType,
  CollectionRef,
  CollectionView,
} from "@/types/collection"
import { PlaceRef } from "@/types/entity"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "@/lib/storage/redux-store"

export interface CollectionSliceState {
  currentView: CollectionView
  currentCollectionRef: CollectionRef | undefined
  currentPage: number
  collaborationDialogOpen: boolean
  filterType: CollectionFilterType
  showNotes: boolean
  noteDialog: {
    isOpen: boolean
    placeRef: PlaceRef | undefined
    currentNote: string | undefined
    isLoading: boolean
  }
}

export const collectionSliceInitialState: CollectionSliceState = {
  currentView: COLLECTION_VIEW_DEFAULT,
  currentCollectionRef: undefined,
  currentPage: 1,
  collaborationDialogOpen: false,
  filterType: "all",
  showNotes: true,
  noteDialog: {
    isOpen: false,
    placeRef: undefined,
    currentNote: undefined,
    isLoading: false,
  },
}

const collectionSlice = createSlice({
  name: "collectionSlice",
  initialState: collectionSliceInitialState,
  reducers: {
    reset: () => collectionSliceInitialState,
    setCurrentCollectionRef: (
      state,
      action: PayloadAction<CollectionRef | undefined>
    ) => {
      state.currentCollectionRef = action.payload
      // Reset page when collection changes
      state.currentPage = 1
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },
    setCurrentView: (state, action: PayloadAction<CollectionView>) => {
      state.currentView = action.payload
    },
    openNoteDialog: (
      state,
      action: PayloadAction<{
        placeRef: PlaceRef
        currentNote?: string
      }>
    ) => {
      state.noteDialog = {
        isOpen: true,
        placeRef: action.payload.placeRef,
        currentNote: action.payload.currentNote,
        isLoading: false,
      }
    },
    closeNoteDialog: (state) => {
      state.noteDialog = {
        isOpen: false,
        placeRef: undefined,
        currentNote: undefined,
        isLoading: false,
      }
    },
    setNoteDialogLoading: (state, action: PayloadAction<boolean>) => {
      state.noteDialog.isLoading = action.payload
    },
    setNoteDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.noteDialog.isOpen = action.payload
    },
    setCollaborationDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.collaborationDialogOpen = action.payload
    },
    setFilterType: (state, action: PayloadAction<CollectionFilterType>) => {
      state.filterType = action.payload
    },
    setShowNotes: (state, action: PayloadAction<boolean>) => {
      state.showNotes = action.payload
    },
  },
})

const selectSliceState = (state: RootState) => state.collectionSlice

// UI state selectors only - server state should be accessed via useCurrentCollection hook

export const selectCurrentCollectionRef = createSelector(
  [selectSliceState],
  (state) => state.currentCollectionRef
)

export const selectCurrentPage = createSelector(
  [selectSliceState],
  (state) => state.currentPage
)

export const selectCurrentView = createSelector(
  [selectSliceState],
  (state) => state.currentView
)

export const selectNoteDialogState = createSelector(
  [selectSliceState],
  (state) => state.noteDialog
)

export const selectCollaborationDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.collaborationDialogOpen
)

export const selectFilterType = createSelector(
  [selectSliceState],
  (state) => state.filterType
)

export const selectShowNotes = createSelector(
  [selectSliceState],
  (state) => state.showNotes
)

export default collectionSlice
